import { useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { type ApiError, toApiError } from '../../lib/api/api-client';
import { useApiClient } from '../../lib/api/api-context';
import { useActionKeys } from '../../lib/api/idempotency-key';

/** The two writes of E11. */
export type AccountWrite = 'logout' | 'disconnect';

/** E11's write state and actions. */
export interface AccountWrites {
  /** The write in flight: its sheet is busy (S8 R3, AC32). */
  readonly pending: AccountWrite | null;
  /** The last failure, shown inside that write's sheet (S8 R21). */
  readonly error: { readonly write: AccountWrite; readonly error: ApiError } | null;
  /** `POST /telegram/logout`; true on success. */
  readonly logout: () => Promise<boolean>;
  /** `DELETE /trading-account`; true on success or on 409 `TRADING_ACCOUNT_NOT_CONNECTED` (A-D5). */
  readonly disconnect: () => Promise<boolean>;
  /** Forgets the last failure (when a sheet opens). */
  readonly clearError: () => void;
}

/** C1 A-D5: the account is already gone, which is what the trader asked for (S8 R15). */
function alreadyDisconnected(error: ApiError): boolean {
  return error.status === 409 && error.reasonCode === 'TRADING_ACCOUNT_NOT_CONNECTED';
}

/**
 * The writes of E11 (S8 R4, R8, R15). One `Idempotency-Key` per write, kept across a retry only
 * after a network failure, a 5xx or `IDEMPOTENCY_KEY_IN_PROGRESS` (`useActionKeys`, S2 R13). One
 * write at a time: a tap while one runs sends nothing.
 */
export function useAccountWrites(): AccountWrites {
  const api = useApiClient();
  const keys = useActionKeys();
  const [pending, setPending] = useState<AccountWrite | null>(null);
  const [error, setError] = useState<AccountWrites['error']>(null);
  const running = useRef(false);

  const run = useCallback(
    async (
      write: AccountWrite,
      send: (idempotencyKey: string) => Promise<void>,
      isDone: (failure: ApiError) => boolean = () => false,
    ): Promise<boolean> => {
      if (running.current) return false;
      running.current = true;
      setPending(write);
      setError(null);
      try {
        await send(keys.keyFor(write));
        keys.settle(write, null);
        return true;
      } catch (caught) {
        const failure = toApiError(caught);
        keys.settle(write, failure);
        if (isDone(failure)) return true;
        setError({ write, error: failure });
        return false;
      } finally {
        running.current = false;
        setPending(null);
      }
    },
    [keys],
  );

  const logout = useCallback(
    () =>
      run('logout', (idempotencyKey) =>
        api.post(
          '/telegram/logout',
          { guard: { kind: 'authenticated', idempotencyKey } },
          z.void(),
        ),
      ),
    [api, run],
  );
  const disconnect = useCallback(
    () =>
      run(
        'disconnect',
        (idempotencyKey) =>
          api.delete(
            '/trading-account',
            { guard: { kind: 'authenticated', idempotencyKey } },
            z.void(),
          ),
        alreadyDisconnected,
      ),
    [api, run],
  );
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  return useMemo(
    () => ({ pending, error, logout, disconnect, clearError }),
    [pending, error, logout, disconnect, clearError],
  );
}
